import React, { FC } from 'react';
import { Link } from 'react-router';
import { Breadcrumbs, Faq, HeroImage } from '../../customFrontend/parts';
import { SiteManifest, SiteManifestPage } from '../../customFrontend/types';
import { useMatureSearchAge } from '../../customFrontend/useMatureSearchAge';
import { CityPills, Cta, FinalCta, SiteFooter } from './Chrome';
import { ChatappCity, ChatappLabels, ChatappTopic } from './types';
import './chatapp.scss';

interface Props {
  site: SiteManifest;
  page: SiteManifestPage;
  topic: ChatappTopic;
  cities: ChatappCity[];
  labels: ChatappLabels;
  topics: ChatappTopic[];
}

/**
 * A chatapp topic page (the mature page): the city page's hero, following the
 * photo (a landscape is the wide banner with the text on a card, a portrait
 * stands beside the text from 992px), in the topic's accent
 * (`.cx-topic--<slug>`); then every city as pills.
 */
const BrandTopicPage: FC<Props> = ({ site, page, topic, cities, labels, topics }) => {
  useMatureSearchAge();
  const wide = page.imageWidth > page.imageHeight;
  const text = (
    <div className="cx-hero-text">
      <Breadcrumbs items={[{ label: site.homeBreadcrumb ?? 'Home', to: '/' }, { label: page.breadcrumb ?? topic.name }]} />
      <p className="cx-eyebrow"><span className="cx-dot" aria-hidden="true" /> {topic.kicker}</p>
      <h1>{topic.h1}</h1>
      <p className="cx-lead">{topic.lead}</p>
      <Cta>{topic.cta.button}</Cta>
    </div>
  );
  return (
    <>
      {wide ? (
        <section className={`cx-hero cx-hero--city cx-hero--wide cx-topic--${topic.slug}`}>
          <figure className="cx-banner">
            <HeroImage page={page} className="cx-banner-img" />
          </figure>
          <div className="container">
            <div className="cx-hero-card">{text}</div>
          </div>
        </section>
      ) : (
        <section className={`cx-hero cx-hero--city cx-hero--portrait cx-topic--${topic.slug}`}>
          <div className="container cx-hero-grid">
            {text}
            <div className="cx-hero-media">
              <figure className="cx-frame cx-frame--portrait">
                <HeroImage page={page} className="cx-frame-img" />
              </figure>
            </div>
          </div>
        </section>
      )}

      <article className={`cx-section cx-city-article cx-topic--${topic.slug}`}>
        <div className="container cx-narrow">
          <div className="cx-intro">{topic.intro}</div>
          {topic.sections.map((section) => (
            <section key={section.title} className="cx-city-section">
              <h2>{section.title}</h2>
              {section.body}
            </section>
          ))}
        </div>
      </article>

      <section className="cx-section cx-section--soft">
        <div className="container cx-narrow">
          <Faq title={topic.faqTitle} items={topic.faq} />
        </div>
      </section>

      <FinalCta {...topic.cta} className={`cx-topic--${topic.slug}`} />

      <section className="cx-section">
        <div className="container cx-narrow">
          <h2 className="text-center">{topic.citiesTitle}</h2>
          <CityPills cities={cities} exclude={topic.path} labels={labels} />
          <p className="cx-back-home"><Link to="/">{labels.backHome}</Link></p>
        </div>
      </section>

      <SiteFooter site={site} cities={cities} labels={labels} topics={topics} />
    </>
  );
};

export default BrandTopicPage;
